import { useEffect, useState } from "react";
import RegistrationsTicker from "@/components/RegistrationsTicker";
import EventRegistrationForm from "@/components/EventRegistrationForm";
import { ASSET, RERA_NUMBER, DEVELOPER } from "@/constants/testIds";
import { CalendarDays, MapPin, Sparkles, Users, ShieldCheck, Compass, ArrowRight, ChevronDown } from "lucide-react";

/**
 * /event — Chapter III launch landing page.
 *
 * Structured as a top-converting event page for Meta-ads traffic:
 *   1. Hero  — punchy headline + date + live registration ticker + CTA
 *   2. Story — the Rohan-Neha-Rihaan narrative on a flowing SVG path
 *   3. Value stack — what Sept 12–13 unlocks
 *   4. Registration form (+ spin-wheel preview embedded on Step 1)
 *   5. Meet the ridge — image gallery
 *   6. Trust bar — MahaRERA, Malpani heritage, 100+ families
 *   7. FAQ — 5 objection-busters
 *   8. Final CTA — scrolls back to the form
 */
export default function Event() {
  return (
    <div>
      <Hero />
      <Story />
      <ValueStack />
      <RegistrationBlock />
      <Gallery />
      <TrustBar />
      <EventFAQ />
      <FinalCTA />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1. Hero
// ---------------------------------------------------------------------------
function Hero() {
  const scrollToForm = () => {
    const el = document.getElementById("event-form");
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <section className="relative min-h-[92vh] bg-void overflow-hidden">
      <img
        src={ASSET.entrance}
        alt="Pali Hill City entrance road, Konkan hills at golden hour"
        className="absolute inset-0 w-full h-full object-cover opacity-95"
        loading="eager"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/50 to-black/85" />
      <div className="relative container-editorial px-5 md:px-10 pt-32 md:pt-40 pb-16 md:pb-24 text-cream">
        <div className="max-w-3xl fade-up">
          <div className="eyebrow text-lime mb-4">
            <span className="editorial-rule bg-lime" /> Chapter III Launch · Sept 12–13, 2026
          </div>
          <h1 className="font-display text-5xl md:text-7xl leading-[0.98]">
            Land. Legacy.<br />Launch.
          </h1>
          <p className="mt-6 max-w-xl text-cream/85 leading-relaxed text-lg">
            1,800–2,000 sq.ft plots · 90 minutes from Mumbai · a hilltop
            community 100+ families already call their weekend home.
          </p>
          <div className="mt-8 flex flex-wrap gap-4 text-sm text-cream/85">
            <span className="inline-flex items-center gap-2"><CalendarDays size={16} className="text-lime" /> Sept 12–13, 2026</span>
            <span className="inline-flex items-center gap-2"><MapPin size={16} className="text-lime" /> Pali Hill City ridge, Raigad</span>
            <span className="inline-flex items-center gap-2"><Users size={16} className="text-lime" /> 200 seats · limited</span>
          </div>
          <div className="mt-8">
            <RegistrationsTicker variant="dark" />
          </div>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <button
              onClick={scrollToForm}
              className="btn-primary text-base"
              data-testid="event-hero-cta"
            >
              Claim my boarding pass <ArrowRight size={16} />
            </button>
            <button
              onClick={() => document.getElementById("event-story")?.scrollIntoView({ behavior: "smooth" })}
              className="inline-flex items-center gap-2 text-sm text-cream/80 hover:text-lime transition-colors"
            >
              Read the story below <ChevronDown size={14} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 2. Story — the Rohan-Neha-Rihaan narrative on a wavy SVG path
// ---------------------------------------------------------------------------
const STORY_BEATS = [
  { text: `Every Friday evening, Rohan and Neha made the same promise.\n"Next month, we'll take Rihaan somewhere nice."` },
  { text: `But next month always became another month.\nWork deadlines. School exams. Family functions. Traffic. Life.` },
  { text: `One Saturday morning, after four hours driving to Lonavala only to find crowded cafés, overflowing parking lots, and packed resorts, Rihaan quietly asked from the back seat —\n"Papa... don't we have a place that's ours?"` },
  { text: `The question stayed with them.\nThat evening, they began searching.` },
  { text: `Farmhouses? Too much maintenance.\nApartments? Still felt like city life.\nFarmland? Too many legal questions.\nReady villas? Beautiful, but beyond their budget.` },
  { text: `They weren't just looking for a property.\nThey were looking for a feeling.\n\nA place where weekends weren't planned months in advance. Where their parents could enjoy fresh air, their son could run barefoot on the grass, and mornings began with birds instead of traffic.` },
  { text: `Weeks later, while scrolling online, Neha came across something that caught her attention.\n\nPali Hill City.\n\nNot another isolated plot. Not another crowded township. A 300+ acre hilltop community where families could build their weekend home at their own pace — with roads, infrastructure, a clubhouse, and a growing community already taking shape.` },
  { text: `Then one line stood out.\n\n"More than 100 families have already chosen Pali Hills over the last three years."\n\nThat made them pause.\n"Why have so many people already invested here?" Rohan wondered.` },
  { text: `The website wasn't asking them to book a plot.\nIt invited them to something else — the Chapter 3 Launch Experience.\n\nA chance to walk the land, understand the vision, meet the team, explore the masterplan, and speak to families who had already begun their journey.` },
  { text: `"Let's not decide online," Neha said.\n"Let's just go and see why everyone is talking about this place."` },
  { text: `Maybe they would come back convinced. Maybe they wouldn't.\nBut for the first time in a long time, they weren't going to spend another weekend sitting in traffic, wondering what life could have been.` },
  { text: `Because sometimes, the first step toward owning your Peace of Land\nisn't buying a plot.\n\nIt's simply showing up to experience it.` },
];

function Story() {
  return (
    <section id="event-story" className="relative bg-offwhite py-24 md:py-32 overflow-hidden">
      {/* Wavy background path — decorative */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        preserveAspectRatio="none"
        viewBox="0 0 100 1200"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M 30 0 Q 70 100 30 200 Q -10 300 30 400 Q 70 500 30 600 Q -10 700 30 800 Q 70 900 30 1000 Q -10 1100 30 1200"
          stroke="url(#story-grad)"
          strokeWidth="0.6"
          strokeDasharray="2 3"
          fill="none"
        />
        <defs>
          <linearGradient id="story-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#A6CE39" stopOpacity="0.6" />
            <stop offset="50%" stopColor="#00A676" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#0B4A3D" stopOpacity="0.6" />
          </linearGradient>
        </defs>
      </svg>

      <div className="relative container-editorial px-5 md:px-10 max-w-3xl mx-auto">
        <div className="text-center mb-16">
          <div className="eyebrow text-teal inline-flex items-center gap-2 justify-center">
            <span className="editorial-rule" /> A short story about weekends
          </div>
          <h2 className="font-display text-forest text-4xl md:text-5xl mt-3 leading-tight">
            One family&apos;s search<br />for a place that&apos;s theirs.
          </h2>
        </div>

        <div className="space-y-14 md:space-y-20">
          {STORY_BEATS.map((beat, i) => (
            <StoryBeat key={i} index={i} text={beat.text} />
          ))}
        </div>

        <div className="mt-20 text-center fade-up">
          <p className="font-display text-forest text-xl md:text-2xl leading-relaxed max-w-2xl mx-auto">
            Join us for the Pali Hill City Chapter 3 Launch Experience and
            discover why 100+ families have already made Pali Hills their
            weekend address of choice.
          </p>
          <div className="mt-8">
            <a
              href="#event-form"
              className="btn-primary text-base"
              data-testid="event-story-cta"
            >
              Reserve your seat <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function StoryBeat({ index, text }) {
  const alignRight = index % 2 === 1;
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && setVisible(true),
      { threshold: 0.2 },
    );
    const node = document.getElementById(`beat-${index}`);
    if (node) obs.observe(node);
    return () => obs.disconnect();
  }, [index]);
  return (
    <div
      id={`beat-${index}`}
      className={`relative transition-all duration-700 ease-out ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      } ${alignRight ? "md:pl-16 md:text-right md:ml-auto" : "md:pr-16"} max-w-xl ${
        alignRight ? "md:mr-0 md:ml-auto" : "md:ml-0"
      }`}
    >
      <div
        className={`absolute top-2 ${alignRight ? "left-2 md:-left-2" : "right-2 md:-right-2"} w-3 h-3 rounded-full bg-teal shadow-[0_0_0_4px_rgba(0,166,118,0.15)]`}
        aria-hidden="true"
      />
      <p className="font-body text-forest/85 text-[16px] md:text-[17px] leading-[1.75] whitespace-pre-line">
        {text}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 3. Value stack
// ---------------------------------------------------------------------------
const VALUE_ITEMS = [
  {
    icon: Compass,
    title: "A guided walk of the ridge",
    body: "Founders and land experts take you through the masterplan, plot sectors, and the peace-of-land ethos in person at golden hour.",
  },
  {
    icon: Users,
    title: "Meet the families already here",
    body: "Speak with residents who already chose Pali Hills over the last three years. No sales pitch — just real answers.",
  },
  {
    icon: ShieldCheck,
    title: "Full paperwork on the table",
    body: "MahaRERA registration, 7/12 title docs, layout approvals — everything you need to trust the land, brought to you.",
  },
  {
    icon: Sparkles,
    title: "An assured launch gift",
    body: "Every registration unlocks one spin on the wheel — Konkan hamper, villa consult, ₹50k voucher, AirPods, weekend stay, or the iPhone 17 grand prize.",
  },
];

function ValueStack() {
  return (
    <section className="section-pad bg-forest text-cream">
      <div className="container-editorial px-5 md:px-10">
        <div className="max-w-2xl mb-14">
          <div className="eyebrow text-lime"><span className="editorial-rule bg-lime" /> What Sept 12–13 unlocks</div>
          <h2 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
            Two golden-hour days.<br />Four reasons to show up.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {VALUE_ITEMS.map((v) => (
            <div
              key={v.title}
              className="bg-void/40 backdrop-blur-sm border border-cream/10 rounded-2xl p-6 md:p-7 hover:border-lime/40 transition-colors"
              data-testid={`event-value-${v.title.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <div className="w-11 h-11 rounded-full bg-lime text-void grid place-items-center mb-4">
                <v.icon size={20} />
              </div>
              <h3 className="font-display text-cream text-xl md:text-2xl leading-tight">
                {v.title}
              </h3>
              <p className="text-cream/70 text-sm mt-2 leading-relaxed">{v.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 4. Registration block (existing form)
// ---------------------------------------------------------------------------
function RegistrationBlock() {
  return (
    <section id="event-form" className="section-pad bg-offwhite">
      <div className="container-editorial px-5 md:px-10 grid md:grid-cols-12 gap-10">
        <aside className="md:col-span-4">
          <div className="eyebrow text-teal mb-4"><span className="editorial-rule" /> Reserve your seat</div>
          <h2 className="font-display text-forest text-3xl md:text-4xl leading-tight">
            Boarding pass<br />+ a spin to win.
          </h2>
          <p className="mt-6 text-forest/70 leading-relaxed">
            Complete a short registration and you&apos;ll instantly receive a
            flight-style boarding pass with your unique ticket number, seat,
            and gate. Then spin the wheel for a launch-day gift.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-forest/75">
            <li>· Sept 12–13, 2026 (choose one)</li>
            <li>· At the Pali Hill City ridge, Raigad</li>
            <li>· Golden hour, both days</li>
            <li>· Founders in person</li>
            <li>· Plot map + full paperwork on the table</li>
            <li>· One free spin per registration — attend to redeem</li>
          </ul>
          <div className="mt-10 border-t border-forest/10 pt-6">
            <RegistrationsTicker />
          </div>
        </aside>
        <div className="md:col-span-7 md:col-start-6">
          <EventRegistrationForm source="event_in_site" />
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 5. Meet the ridge — image gallery
// ---------------------------------------------------------------------------
function Gallery() {
  const shots = [
    { src: ASSET.heroSarasgad, alt: "Sarasgad fort viewed from the ridge at sunrise", tall: true },
    { src: ASSET.cSector, alt: "C-sector plots demarcated on the plateau" },
    { src: ASSET.conceptVilla, alt: "Concept villa design on a Pali Hill plot" },
    { src: ASSET.entrance, alt: "The road that curves into Pali Hill City" },
  ];
  return (
    <section className="section-pad bg-cream/40">
      <div className="container-editorial px-5 md:px-10">
        <div className="max-w-2xl mb-10">
          <div className="eyebrow text-teal"><span className="editorial-rule" /> Meet the ridge</div>
          <h2 className="font-display text-forest text-4xl md:text-5xl mt-3 leading-tight">
            What you&apos;ll actually walk.
          </h2>
          <p className="mt-4 text-forest/70 text-sm md:text-base leading-relaxed">
            The photos below are the exact ridge, entrance road, and plot
            sectors you&apos;ll experience on Sept 12–13. No renders. No stock.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {shots.map((s, i) => (
            <div
              key={i}
              className={`relative overflow-hidden rounded-xl bg-forest/10 group ${
                s.tall ? "row-span-2 aspect-[3/5]" : "aspect-[4/3]"
              }`}
            >
              <img
                src={s.src}
                alt={s.alt}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="absolute bottom-2 left-3 right-3 text-cream text-[11px] opacity-0 group-hover:opacity-100 transition-opacity leading-tight">
                {s.alt}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 6. Trust bar
// ---------------------------------------------------------------------------
function TrustBar() {
  return (
    <section className="section-pad bg-offwhite border-t border-forest/10">
      <div className="container-editorial px-5 md:px-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
          <TrustStat label="MahaRERA registered" value={RERA_NUMBER} mono />
          <TrustStat label="Master developer" value={DEVELOPER?.name || "Malpani Group"} />
          <TrustStat label="Families onboarded" value="100+" note="Across Chapters I & II" />
          <TrustStat label="Total community" value="300+ acres" note="Hilltop, Konkan corridor" />
        </div>
      </div>
    </section>
  );
}

function TrustStat({ label, value, note, mono }) {
  return (
    <div>
      <div className="eyebrow text-teal">{label}</div>
      <div className={`font-display text-forest text-2xl md:text-3xl mt-2 leading-tight ${mono ? "font-mono text-lg md:text-xl" : ""}`}>
        {value}
      </div>
      {note && <div className="text-forest/50 text-xs mt-1">{note}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 7. FAQ
// ---------------------------------------------------------------------------
const EVENT_FAQS = [
  {
    q: "How far is Pali Hill City from Mumbai and Pune?",
    a: "About 90 minutes from Panvel (Navi Mumbai) via the Mumbai-Goa highway. From central Pune, expect roughly 2.5 hours. Google Maps: search &apos;Pali Hill City Raigad&apos; — driving directions are exact.",
  },
  {
    q: "Is Pali Hill City MahaRERA registered?",
    a: `Yes. Registration number ${RERA_NUMBER} — verifiable on the official MahaRERA portal. We'll have the physical registration certificate on the table at the launch event.`,
  },
  {
    q: "What if I can't attend on Sept 12 or 13?",
    a: "Register anyway. If you can't make it, our sales team will arrange a private, guided site visit within 30 days of the launch — same walk, same paperwork, same team.",
  },
  {
    q: "What&apos;s the plot size and layout?",
    a: "Plots range from 1,800 to 2,000 sq.ft. Every plot is demarcated on the ground, has road access, and comes with underground utility conduits. Full plot map and dimensions handed over at the event.",
  },
  {
    q: "Is my registration binding? Do I have to book a plot?",
    a: "Zero obligation. Registration only reserves your seat for the site walk and unlocks your assured spin-wheel gift (redeemable only if you attend and later choose to book).",
  },
];

function EventFAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section className="section-pad bg-offwhite">
      <div className="container-editorial px-5 md:px-10 max-w-3xl">
        <div className="mb-10">
          <div className="eyebrow text-teal"><span className="editorial-rule" /> Common questions</div>
          <h2 className="font-display text-forest text-4xl md:text-5xl mt-3 leading-tight">
            Everything you&apos;d sensibly want to know before you drive out.
          </h2>
        </div>
        <div className="space-y-3">
          {EVENT_FAQS.map((f, i) => (
            <div key={i} className="border border-forest/15 rounded-xl overflow-hidden">
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-cream/40 transition-colors"
                data-testid={`event-faq-${i}`}
              >
                <span className="font-display text-forest text-base md:text-lg">{f.q}</span>
                <ChevronDown
                  size={18}
                  className={`text-forest/50 transition-transform ${open === i ? "rotate-180" : ""}`}
                />
              </button>
              {open === i && (
                <div className="px-5 pb-5 text-forest/75 text-sm md:text-[15px] leading-relaxed">
                  {f.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 8. Final CTA
// ---------------------------------------------------------------------------
function FinalCTA() {
  const scrollToForm = () =>
    document.getElementById("event-form")?.scrollIntoView({ behavior: "smooth" });
  return (
    <section className="relative py-24 md:py-28 bg-void text-cream overflow-hidden">
      <div className="absolute inset-0 opacity-30">
        <img
          src={ASSET.heroSarasgad}
          alt=""
          className="w-full h-full object-cover"
          aria-hidden="true"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-void/70 via-void/85 to-void/95" />
      <div className="relative container-editorial px-5 md:px-10 text-center max-w-3xl mx-auto">
        <div className="eyebrow text-lime justify-center inline-flex">
          <span className="editorial-rule bg-lime" /> Sept 12–13, 2026
        </div>
        <h2 className="font-display text-cream text-4xl md:text-6xl mt-4 leading-[1.05]">
          The next weekend that <em className="text-lime not-italic">actually</em> changes something.
        </h2>
        <p className="mt-6 text-cream/75 text-base md:text-lg leading-relaxed">
          Two days at the ridge. One family story that might become yours.
          200 seats total — reserve yours in under 60 seconds.
        </p>
        <div className="mt-10">
          <RegistrationsTicker variant="dark" />
        </div>
        <div className="mt-10">
          <button
            onClick={scrollToForm}
            className="btn-primary text-base"
            data-testid="event-final-cta"
          >
            Claim my boarding pass <ArrowRight size={16} />
          </button>
        </div>
        <div className="mt-6 text-cream/60 text-sm">
          Can't travel on Sept 12–13?{" "}
          <a
            href="/reserve"
            data-testid="event-final-reserve-link"
            className="text-lime underline underline-offset-4 hover:text-white transition-colors"
          >
            Reserve online with a refundable EOI
          </a>{" "}
          — lock launch pricing until Dussehra.
        </div>
      </div>
    </section>
  );
}
